<template>
  <Transition name="slide-fade" appear>
    <div v-if="visible" class="modal">
      <div class="modal-content">
        <div class="form-container">
          <div class="form-section">
            <div>
              <h2 class="page-title">{{ $t('task_information') }}</h2>
              <h5 class="page-description">{{ $t('task_information_description') }}</h5>
            </div>
            <div class="form-div">
              <span class="form-title">{{ $t('task_name') }}</span>
              <input class="form-input" :placeholder="$t('enter_task_name')" v-model="taskData.name" />
            </div>

            <div class="form-div">
              <span class="form-title">{{ $t('task_description') }}</span>
              <textarea
                  class="form-input"
                  :placeholder="$t('enter_task_description')"
                  v-model="taskData.description"
                  rows="3"
                  style="min-height: 200px"
              ></textarea>
            </div>

            <div class="form-div">
              <span class="form-title">{{ $t('task_points') }}</span>
              <div class="input-with-icon">
                <img src="@/assets/images/xp-coin.svg" alt="Point" class="point-icon" />

                <input
                    type="number"
                    class="form-input with-icon"
                    :placeholder="$t('enter_task_points')"
                    v-model="taskData.xpPoints"
                    min="1"
                />
              </div>
            </div>

            <div class="status-toggle">
              <span class="form-title">{{ $t('task_status') }}</span>
              <div class="toggle-switch">
                <label class="switch">
                  <input type="checkbox" v-model="taskData.isActive">
                  <span class="slider round"></span>
                </label>
                <span class="status-label">{{ taskData.isActive ? $t('active_task') : $t('passive_task') }}</span>
              </div>
            </div>
          </div>

          <div class="img-preview">
            <div class="task-image-preview">
              <img :src="taskData.iconUrl" alt="Task image" class="preview-image" />
            </div>
          </div>
        </div>

        <div class="action-buttons">
          <div class="right-buttons">
            <button class="cancel-button" @click="onHide">{{ $t('cancel') }}</button>
            <button class="save-button" @click="saveTask">
              <span v-if="!isLoading">{{ $t('save') }}</span>
              <VueSpinnerIos v-if="isLoading" size="20" height="100%" color="#fff" />
            </button>
          </div>
        </div>

        <div @click="onHide" class="close-btn">
          <img class="cross" src="@/assets/images/cross.svg" />
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import { VueSpinnerIos } from 'vue3-spinners';
import gamificationService from '@/service/gamificationService';
import { handleErrorResponse } from '@/utils/utils';

export default {
  name: 'TaskEditModal',
  components: {
    VueSpinnerIos
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    task: {
      type: Object,
      required: true
    },
    onHide: {
      type: Function,
      default: () => {}
    },
    onSuccess: {
      type: Function,
      default: () => {}
    },
    onDelete: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      isLoading: false,
      taskData: {
        taskId: '',
        name: '',
        description: '',
        xpPoints: 0,
        iconUrl: '',
        isActive: true,
        taskType: '',
        taskIdentifier: ''
      }
    };
  },
  watch: {
    task: {
      immediate: true,
      handler(newTask) {
        if (newTask) {
          this.taskData = { ...newTask };
        }
      }
    }
  },
  methods: {
    async saveTask() {
      try {
        this.isLoading = true;

        const response = await gamificationService.updateGamificationTask(this.taskData);

        if (response.data.responseType === 'success') {
          console.log('Task updated successfully:', response.data);
          this.$snackbar.add({
            text: response.data.message || this.$t('task_updated_successfully'),
            type: 'success'
          });
          this.onSuccess(this.taskData);
        } else {
          this.$snackbar.add({
            text: response.data.message || this.$t('task_update_error'),
            type: 'error'
          });
        }
      } catch (error) {
        handleErrorResponse(error);
        this.$snackbar.add({
          text: this.$t('task_update_error'),
          type: 'error'
        });
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10001 !important;
}

.modal-content {
  background-color: white;
  border-radius: 12px;
  width: 1160px;
  max-width: 95%;
  position: relative;
  padding: 32px;
  max-height: 90vh;
  overflow-y: auto;
}

.page-description {
  font-size: 14px;
  color: #475467;
  font-family: 'euclid_medium', sans-serif;
}

.form-container {
  display: flex;
  height: 100% !important;
  gap: 40px;
  margin-top: 30px;
}

.form-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: space-evenly;
}

.form-div {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.form-title {
  font-size: 14px;
  font-weight: 500;
  color: #344054;
}

.img-preview {
  width: 300px;
}

.task-image-preview {
  width: 300px;
  background-color: #F2F4F7;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.status-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-top: 1px solid #EAECF0;
  border-bottom: 1px solid #EAECF0;
  margin: 24px 0;
}

.toggle-switch {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Toggle Switch */
.switch {
  position: relative;
  display: inline-block;
  width: 52px;
  height: 28px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #E4E7EC;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 22px;
  width: 22px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
}

input:checked + .slider {
  background-color: #12B76A;
}

input:focus + .slider {
  box-shadow: 0 0 1px #12B76A;
}

input:checked + .slider:before {
  transform: translateX(24px);
}

.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

.status-label {
  font-size: 14px;
  color: #344054;
  font-weight: 500;
}

.action-buttons {
  display: flex;
  justify-content: end;
  align-items: center;
  margin-top: 16px;
}

.delete-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.delete-btn:hover {
  background-color: #FEF3F2;
}

.delete-icon {
  width: 18px;
  height: 18px;
}

.delete-text {
  color: #D92D20;
  font-size: 14px;
  font-weight: 500;
}

.right-buttons {
  display: flex;
  gap: 12px;
}

.cancel-button {
  background-color: #FFFFFF;
  border: 1px solid #D0D5DD;
  border-radius: 8px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  color: #344054;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cancel-button:hover {
  background-color: #F9FAFB;
}

.save-button {
  background-color: #0052CC;
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s;
}

.save-button:hover {
  background-color: #0047B3;
}

.close-btn {
  position: absolute;
  top: 8px;
  right: 6px;
  cursor: pointer;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: background-color 0.2s;
  background-color: #f2f4f7;
  margin: 10px;
}

.cross {
  width: 16px;
  height: 16px;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

@media (max-width: 768px) {
  .form-container {
    flex-direction: column;
  }

  .img-preview {
    width: 100%;
    margin-top: 24px;
  }

  .task-image-preview {
    width: 100%;
    height: 250px;
  }
}

.input-with-icon {
  position: relative;
  width: 100%;
}

.form-input.with-icon {
  padding-left: 40px;
}

.point-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  pointer-events: none;
}
</style>
